import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { OverlayLoginComponent } from 'src/app/authentication/components/overlay-login/overlay-login.component';
import { UserSession } from 'src/app/core/models/user.model';
import { AddToCart, Cart, UpdateCart } from 'src/app/core/services/cart/cart-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { UserService } from 'src/app/core/services/user/user.service';

import { Router } from '@angular/router';
import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { PendingCartService } from 'src/app/core/services/pending-cart/pending-cart.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { ProductListingVariant } from 'src/app/products/models/product-listing.model';
import { AddToCartInputComponent } from "../add-to-cart-input/add-to-cart-input.component";

@Component({
  standalone: true,
  selector: 'webstore-add-to-cart-action',
  imports: [CommonModule, AddToCartInputComponent],
  templateUrl: './add-to-cart-action.component.html',
  styleUrls: ['./add-to-cart-action.component.scss'],
})
export class AddToCartActionComponent {
  private readonly userService = inject(UserService);
  private readonly overlayService = inject(OverlayService);
  private readonly cartRepositoryService = inject(CartRepositoryService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly platformService = inject(PlatformService);
  private readonly pendingCartService = inject(PendingCartService);
  private readonly conversionTrackingService = inject(ConversionTrackingService);
  private readonly adsManagementService = inject(AdsManagementService);

  private _loginRef = signal<MyOverlayRef<OverlayLoginComponent> | null>(null);
  readonly loginRef = this._loginRef.asReadonly();

  readonly errorMessage = signal<string>('');
  readonly user = signal<UserSession>({} as UserSession);
  // Error signal for handling error messages
  readonly errorSignal = signal<string>('');

  readonly isDisplayPlusImage = signal<boolean>(false);
  readonly productSignal = signal<ProductListingVariant>({} as ProductListingVariant);
  readonly isDisplayAddToCartText = signal<boolean>(false);

  addToCartClicked$ = output<boolean>();

  private _cart = signal<Cart | null>(null);
  readonly cart = this._cart.asReadonly();

  readonly isBrowser = computed(() => this.platformService.isBrowser());

  readonly product = computed(() => this.productSignal());

  readonly isProductInCart = computed(() =>
    this.cart()?.cart_items?.some(cartItem => cartItem.product.id === this.product().id) ?? false
  );

  readonly matchedCartItem = computed(() => {
    const cart = this.cart();
    const product = this.product();
    if (cart && cart.cart_items && product) {
      return cart.cart_items.find(
        item => item.product.id === product.id && !item.is_product_offer_item
      ) || null;
    }
    return null;
  });



  readonly isDisplayPlusImageInput = input<boolean>(false);
  readonly productInput = input<ProductListingVariant>({} as ProductListingVariant);
  readonly isDisplayAddToCartTextInput = input<boolean>(false);

  constructor() {

    effect(() => {
      const cart = this.cartRepositoryService.cart();
      this._cart.set(cart);
    });

    effect(() => {
      const isDisplayPlusImageValue = this.isDisplayPlusImageInput();
      this.isDisplayPlusImage.set(isDisplayPlusImageValue);
    });

    effect(() => {
      const productValue = this.productInput();
      this.productSignal.set(productValue);
    });

    effect(() => {
      const isDisplayAddToCartTextValue = this.isDisplayAddToCartTextInput();
      this.isDisplayAddToCartText.set(isDisplayAddToCartTextValue);
    });

    effect(() => {
      const user = this.userService.appUser;
      this.user.set(user || ({} as UserSession));
    });

  }


  searchCart(product: ProductListingVariant): void {
    const user = this.user();

    if (!user?.access_token) {
      this.addToCartClicked$.emit(true);

      // Store product information for auto-add after login using PendingCartService
      this.pendingCartService.storePendingProduct({
        id: product.id,
        bid_price: 0,
        quantity: 1,
        productDetailOverlay: false,
        stayOnCurrentPage: true
      });

      // Navigate to auth with return URL
      const currentUrl = this.router.url;
      this.router.navigate(['/auth/login-otp'], {
        queryParams: { returnUrl: currentUrl }
      });
    } else {
      this._addToCart(product.id, 0, 1);
    }
  }

  private async _addToCart(id: string, bid_price = 0, quantity = 1, overlayOpened = false): Promise<void> {
    // Service is auto-initialized and always ready

    this.cartRepositoryService.setPageMasking(true);
    // Get UTM and code details for attribution tracking
    const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
    const cart: AddToCart = {
      cart_items: [
        {
          product: id,
          quantity: quantity,
          ...(bid_price && bid_price > 0 && {
            bid_price
          }),
        }
      ],
      ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
        attribution_data: {
          ...collectedUtmAndCodeDetails
        }
      })
    };

    // Use service state management pattern - no try-catch needed
    await this.cartRepositoryService.addProductToCart(cart);

    // Success case - always show success message
    if (overlayOpened) {
      this.addToCartClicked$.emit(false);
    }
    this.cartRepositoryService.setPageMasking(false);
    // Showing success notification for regular cart add
    this.notificationService.showSuccess('Product added to cart successfully.');

    // Track conversion (assuming the cart was updated successfully)
    const currentCart = this.cartRepositoryService.cart();
    if (currentCart) {
      this.conversionTrackingService.trackAddToCartConversion(currentCart);
    }
  }

  increaseQuantity(quantity: number, cartItemId: string): void {
    this.updateQuantity(quantity, cartItemId);
  }

  decreaseQuantity(quantity: number, cartItemId: string): void {
    if (quantity === 0) {
      this.removeCartItem(cartItemId);
    } else {
      this.updateQuantity(quantity, cartItemId);
    }
  }

  async removeCartItem(cartItemId: string): Promise<void> {
    // Service is auto-initialized and always ready

    this.cartRepositoryService.setPageMasking(true);

    // Use service state management pattern - no try-catch needed
    await this.cartRepositoryService.removeProductFromCart(cartItemId);
    this.cartRepositoryService.setPageMasking(false);
  }

  async updateQuantity(quantity: number, cartItemId: string): Promise<void> {
    // Service is auto-initialized and always ready

    const reqObj: UpdateCart = {
      cart_items: [{
        id: cartItemId,
        quantity
      }]
    };

    this.cartRepositoryService.setPageMasking(true);

    try {
      // Use service state management pattern
      await this.cartRepositoryService.updateCart(reqObj);
    } catch (error: any) {

      // Extract the backend error message from the response structure
      let errorMessage = 'Failed to update cart quantity';

      if (error?.error?.message) {
        // Backend validation error - use the message directly
        errorMessage = error.error.message;
      } else if (error?.message) {
        // Generic error
        errorMessage = error.message;
      }

      this.errorSignal.set(errorMessage);
      this.notificationService.showError(errorMessage);
    } finally {
      // Always disable page masking and button loading when operation is complete
      this.cartRepositoryService.setPageMasking(false);
      this.cartRepositoryService.setButtonLoading(false);
    }
  }
}

